"use client";

import {
  GlobalCanvas,
  SmoothScrollbar,
} from "@/mono/third-party/r3f-scroll-rig-custom";
import { StatsGl } from "@react-three/drei";
import { useFrame, useThree } from "@react-three/fiber";
import { usePathname } from "next/navigation";
import { Fragment, Suspense, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { NoToneMapping } from "three";
import { create } from "zustand";
import { events } from "../common/constants";
import { Footer } from "../components/Footer/Footer";
import Menu, { menuStore } from "../components/Menu/Menu";
import NavigationBar from "../components/Navigation/NavigationBar";
import VideoPlayer from "../components/VideoPlayer";

import Workers from "mono/lib/webworkers/Workers";
import { PageTransitions } from "mono/third-party/react-page-transitions-custom";
import dynamic from "next/dynamic";
import LoaderView from "../components/views/LoaderView";
import { DefaultLoadingManager as ThreeLoadingManager } from "three";
import { setThreeLoadingManager } from "@/lib/pageReady";

// Wire Three.js loading manager early so all assets are tracked from the start
setThreeLoadingManager(ThreeLoadingManager);
import usePerformance from "../mono/lib/gl/hooks/usePerformance";
import { useDebug } from "../mono/lib/hooks/useDebug";
import useEvent from "../mono/lib/hooks/useEvent";
import useMouseMove from "../mono/lib/hooks/useMouseMove";
import useProjectSetup from "../mono/lib/hooks/useProjectSetup";
import { useSyncRAFs } from "../mono/lib/hooks/useRaf";
import GlobalStyles from "../mono/lib/styles/globalStyles";
import { setVh } from "../mono/lib/styles/mixins";
import { useGlobalStore } from "../mono/store/global";
import { ThemeWrapper } from "./ThemeWrapper";
import {
  useBackForward,
  useNavigationDirection,
  usePreventBack,
} from "mono/lib/hooks/useDetectBackForwardNext";
import emitter from "mono/lib/emitter";
import useUpdateEffect from "mono/lib/hooks/useUpdateEffect";
import CookieBanner from "components/CookieBanner";
import { useScrollbar } from "mono/third-party/r3f-scroll-rig-custom";

export const useThreeOutsideCanvasStore = create(() => ({
  gl: null,
  camera: null,
}));

export const overlayDuration = 500;

const OffScreenCanvas = dynamic(
  () => import("../mono/components/gl/OffScreenCanvas"),
  {
    ssr: false,
  }
);

function CanvasSetup() {
  const gl = useThree((s) => s.gl);
  const camera = useThree((s) => s.camera);

  const pathname = usePathname();

  const debug = useDebug();
  const { update } = usePerformance(gl, debug);

  useEffect(() => {
    useThreeOutsideCanvasStore.setState({ gl, camera });
  }, [gl, camera]);

  useEffect(() => {
    gl.debug.checkShaderErrors = true;
  }, []);

  // Clear the entire backbuffer on route changes to avoid stale pixels from previous views
  useUpdateEffect(() => {
    gl.setRenderTarget(null);
    // Clear should not be clipped by scissor, and we don't change viewport
    gl.setScissorTest(false);
    gl.clear(true, true, true);
  }, [pathname, gl]);

  useFrame(() => {
    if (debug) update();
  });

  const props = {
    trackGPU: true,
    trackHz: true,
    trackCPT: true,
    logsPerSecond: 4,
    graphsPerSecond: 30,
    samplesLog: 40,
    samplesGraph: 10,
    precision: 2,
    horizontal: false,
    minimal: false,
    mode: 2,
  };

  useSyncRAFs();

  if (debug) return <StatsGl {...props} />;
}

// const StyledGLBackgroundCanvas = memo(({ isGLReady }) => (
//   <Canvas frameloop={isGLReady ? "always" : "never"}>
//     <GLBackground />
//   </Canvas>
// ));

// TODO: Make this easier
// const TransitionOverlay = memo(({ overlayRef, glWrapperRef }) => {
//   const openGLBackground = useCallback(async () => {
//     overlayRef.current.style.display = "block";
//     await wait(10);
//     //TODO: overlayRef.current.classList.add("active");
//     useGlobalStore.getState().lenis.stop();
//     glWrapperRef.current.classList.add("visible");
//     emitter.emit(customEvents.GL_BACKGROUND_OPEN);
//   }, []);

//   const closeGLBackground = useCallback(async () => {
//     useGlobalStore.getState().lenis.start();
//     //TODO: overlayRef.current.classList.remove("active");
//     await wait(400);
//     emitter.emit(customEvents.GL_BACKGROUND_CLOSE);
//     await wait(WAIT_DURATION);
//     glWrapperRef.current.classList.remove("visible");
//     overlayRef.current.style.display = "none";
//   }, []);

//   useEvent(customEvents.ROUTE_CHANGE_START, openGLBackground, true);
//   useEvent(customEvents.ROUTE_CHANGE_COMPLETE, closeGLBackground, true);
//   useEvent(customEvents.MENU_OPEN, openGLBackground, true);
//   useEvent(customEvents.MENU_CLOSE, closeGLBackground, true);

//   return <Overlay ref={overlayRef} />;
// });

export default function ClientLayout({ children }) {
  const wrapper = useRef();

  const glWrapper = useRef();
  const scrollBarRef = useRef();

  const pathname = usePathname();

  const [scrollEnabled, setScrollEnabled] = useState(false);

  useProjectSetup();
  useMouseMove();

  const debug = useDebug();

  const { __lenis: lenis } = useScrollbar();

  useEffect(() => {
    if (typeof window !== "undefined") {
      const version = process.env.NEXT_PUBLIC_APP_VERSION;
      const sha = process.env.NEXT_PUBLIC_APP_COMMIT_SHA;
      if (version) {
        // eslint-disable-next-line no-console
        console.log(`[nine-ca] version: ${version}${sha ? ` (${sha})` : ""}`);
      }
    }
  }, []);

  useEffect(() => {
    useGlobalStore.setState({ lenis: scrollBarRef.current });
    //scrollBarRef.current.stop();
  }, []);

  useEvent(events.ROUTE_CHANGE_START, () => {
    if (!Workers.offscreenCanvas || menuStore.getState().isOpen) return;
    Workers.animateGLBackgroundIn();
  });
  // useEvent(
  //   events.ROUTE_CHANGE_COMPLETE,
  //   () => {
  //     if (!Workers.offscreenCanvas) return;
  //     Workers.animateGLBackgroundOut();
  //   },
  //   true
  // );
  useEvent(events.LOADING_OUT_COMPLETE, () => {
    setScrollEnabled(true);
  });

  useEvent(
    events.ANIMATE_GL_BACKGROUND_IN_COMPLETE,
    () => {
      console.log(
        "[ClientLayout] ANIMATE_GL_BACKGROUND_IN_COMPLETE AND SCROLL TO TOP"
      );
      window.scrollTo(0, 0);
      window.history.scrollRestoration = "manual";
      console.log("[ClientLayout] scrollBarRef.current", scrollBarRef.current);
      scrollBarRef.current.scrollTo(0, { immediate: true });
    },
    false,
    [scrollBarRef.current]
  );

  // usePreventBack(true);
  useNavigationDirection({
    onBack: () => {
      emitter.emit(events.ROUTE_CHANGE_START, { route: pathname });
    },
    onForward: () => {
      emitter.emit(events.ROUTE_CHANGE_START, { route: pathname });
    },
  });

  return (
    <ThemeWrapper>
      <GlobalStyles />
      <LoaderView />

      <StyledGLBackground ref={glWrapper}>
        <OffScreenCanvas />
      </StyledGLBackground>

      <Wrapper ref={wrapper}>
        {/* <TransitionOverlay overlayRef={overlay} glWrapperRef={glWrapper} /> */}

        <GlobalCanvas
          eventSource={wrapper}
          eventPrefix="client"
          debug={debug}
          useScrollSync={true}
          //dpr={dpr}
          gl={{
            toneMapping: NoToneMapping,
            antialias: false,
          }}
        >
          {(globalChildren) => (
            <>
              <CanvasSetup />
              <Suspense fallback={null}>{globalChildren}</Suspense>
            </>
          )}
        </GlobalCanvas>

        <SmoothScrollbar
          ref={scrollBarRef}
          enabled={scrollEnabled}
          config={{
            syncTouch: true,
            syncTouchLerp: 0.1,
            touchInertiaMultiplier: 10,
            lerp: 0.07,
          }}
        />

        <VideoPlayer />
        <Menu />
        <NavigationBar />
        <CookieBanner />

        <MainInner>
          <PageTransitions
            pageName={pathname}
            mode="sync"
            detectAnimationEnd={false}
            detectTransitionEnd={false}
            //timeout={{ appear: 0, enter: 1200, exit: 1200 }}
          >
            <Fragment key={pathname}>{children}</Fragment>
            {/* {cloneElement(children, { key: pathname })} */}
          </PageTransitions>
          <Footer />
        </MainInner>
      </Wrapper>
    </ThemeWrapper>
  );
}

// ClientLayout.whyDidYouRender = true;

const MainInner = styled.div`
  margin-top: ${({ theme }) => theme.misc.navigationHeight()};
  background-color: ${({ theme }) => theme.theme.background};
`;

const Wrapper = styled.div`
  touch-action: auto;
  /* transition: transform 1s cubic-bezier(0.22, 1, 0.36, 1); */
  /* transform-origin: 50% 0%; */
  background-color: ${({ theme }) => theme.theme.background};
  overflow: hidden;
`;

const StyledGLBackground = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 7;
  width: 100vw;
  height: calc(${setVh(100)});
  overflow: hidden;
  pointer-events: none;

  opacity: 1;
  /* visibility: hidden; */

  /* &.visible {
    opacity: 1;
    visibility: visible;
  } */

  canvas {
    width: 100%;
    height: 100%;
    position: absolute;
  }
`;

export const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  z-index: 6;
  width: 100vw;
  height: 100vh;
  background-color: rgba(0, 0, 0, 0.8);
  display: none;

  opacity: 0;
  transition: opacity ${overlayDuration / 1000}s cubic-bezier(0.22, 1, 0.36, 1);

  &.active {
    opacity: 1;
  }
`;
